import { ApolloError, WatchQueryFetchPolicy } from '@apollo/client';
import {
  GetRecordsResponseViewFragment,
  useExecuteEqlLazyQuery,
} from 'generated/graphql';
import { useCallback } from 'react';
import { AndOfErrors } from 'v2/core/errors/errors';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import {
  AndStatuses,
  QueryStatus,
  UseExecuteEQLInput,
} from 'v2/core/repository/edge-server/types';
import { useFetchMore } from 'v2/core/repository/edge-server/utils/records';

interface UseExecuteLazyEQLOptions {
  input: UseExecuteEQLInput;
  skipDefaultVariables?: boolean; // Added for backward compatability, using `variables` upfront will trigger unwanted API call on using the hook
  onError?: (error: ApolloError) => void;
  fetchPolicy?: WatchQueryFetchPolicy;
  nextFetchPolicy?: WatchQueryFetchPolicy;
}

const useExecuteLazyEQL = ({
  input,
  skipDefaultVariables = false,
  onError,
  fetchPolicy,
  nextFetchPolicy,
}: UseExecuteLazyEQLOptions) => {
  const { eql, status: sStatus, error: sError } = useGetEQLExpression(input);

  const [
    lazyFunction,
    {
      data,
      loading,
      error: eError,
      called,
      fetchMore: recordFetchMore,
      refetch,
    },
  ] = useExecuteEqlLazyQuery({
    variables: skipDefaultVariables
      ? undefined
      : {
          tenantID: input.tenantID,
          cursor: input.cursor,
          expression: eql ?? '',
        },
    fetchPolicy: fetchPolicy ?? 'network-only',
    nextFetchPolicy,
    onError,
  });

  const eStatus = apolloStatusToQueryStatus(loading, called, undefined, eError);

  const cursor = (data?.executeEQL?.result as GetRecordsResponseViewFragment)
    ?.cursor;
  const [isLoadingMore, fetchMore] = useFetchMore(recordFetchMore, cursor);

  const status = AndStatuses([sStatus, eStatus]);
  const error = AndOfErrors([sError, eError]);

  const lazyFunctionWrapper = useCallback(() => {
    if (!eql) {
      return;
    }

    return lazyFunction({
      variables: {
        tenantID: input.tenantID,
        cursor: input.cursor,
        expression: eql ?? '',
      },
    } as any); // TODO, need to understand why fetchPolicy is not honoured in the generated types
  }, [eql, lazyFunction, input.tenantID, input.cursor]);

  return [
    lazyFunctionWrapper,
    {
      data:
        status === QueryStatus.SUCCESS_COMPLETED
          ? data?.executeEQL?.result
          : undefined,
      error,
      fetchMore,
      isLoadingMore,
      status,
      hasMore: !!cursor,
      refetch,
    },
  ] as const;
};

export default useExecuteLazyEQL;
